import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  providerCustomerPreviewService,
  type ChangePlanRequest,
  type SelectSubscriptionRequest,
  type UsageRecordsQuery,
} from '@/services/provider/provider-customer-preview.service';
import { subscriptionPlanOptionsKeys } from '@/hooks/provider/useSubscriptionPlanOptions';
import {
  InvoicesResponse,
  PayLinkResponse,
  PaymentMethodSummary,
  ProviderSubscriptionPreview,
  UsageRecordsResponse,
  UsageSummary,
} from '@/types/provider-customer-preview';

const customerPreviewKeys = {
  all: ['provider', 'customer-preview'] as const,
  subscription: (customerId?: string) => [...customerPreviewKeys.all, 'subscription', customerId ?? ''] as const,
  usageSummary: (customerId?: string) => [...customerPreviewKeys.all, 'usage-summary', customerId ?? ''] as const,
  usageRecords: (params: UsageRecordsQuery | undefined) =>
    [
      ...customerPreviewKeys.all,
      'usage-records',
      params?.customerId ?? '',
      params?.metric ?? '',
      params?.endpointId ?? '',
      params?.startDate ?? '',
      params?.endDate ?? '',
      params?.sort ?? '',
      params?.page ?? 1,
      params?.perPage ?? 10,
    ] as const,
  invoices: (customerId?: string, limit?: number) =>
    [...customerPreviewKeys.all, 'invoices', customerId ?? '', limit ?? 3] as const,
  paymentMethod: (customerId?: string) =>
    [...customerPreviewKeys.all, 'payment-method', customerId ?? ''] as const,
};

export function usePreviewSubscription(customerId: string | undefined) {
  return useQuery<ProviderSubscriptionPreview>({
    queryKey: customerPreviewKeys.subscription(customerId),
    queryFn: () => providerCustomerPreviewService.getSubscription(customerId as string),
    enabled: Boolean(customerId),
    staleTime: 60 * 1000,
  });
}

export function usePreviewUsageSummary(customerId: string | undefined) {
  return useQuery<UsageSummary>({
    queryKey: customerPreviewKeys.usageSummary(customerId),
    queryFn: () => providerCustomerPreviewService.getUsageSummary(customerId as string),
    enabled: Boolean(customerId),
    staleTime: 30 * 1000,
  });
}

export function usePreviewUsageRecords(params: UsageRecordsQuery | undefined) {
  return useQuery<UsageRecordsResponse>({
    queryKey: customerPreviewKeys.usageRecords(params),
    queryFn: () => providerCustomerPreviewService.getUsageRecords(params as UsageRecordsQuery),
    enabled: Boolean(params?.customerId),
    placeholderData: keepPreviousData,
  });
}

export function usePreviewInvoices(customerId: string | undefined, limit = 3) {
  return useQuery<InvoicesResponse>({
    queryKey: customerPreviewKeys.invoices(customerId, limit),
    queryFn: () => providerCustomerPreviewService.getInvoices(customerId as string, limit),
    enabled: Boolean(customerId),
    staleTime: 60 * 1000,
  });
}

export function usePreviewPaymentMethod(customerId: string | undefined) {
  return useQuery<PaymentMethodSummary>({
    queryKey: customerPreviewKeys.paymentMethod(customerId),
    queryFn: () => providerCustomerPreviewService.getPaymentMethod(customerId as string),
    enabled: Boolean(customerId),
    staleTime: 60 * 1000,
  });
}

export function useChangeSubscriptionPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      subscriptionId,
      customerId,
      ...request
    }: { subscriptionId: string; customerId?: string } & ChangePlanRequest) =>
      providerCustomerPreviewService.changePlan(subscriptionId, request),
    onSuccess: (_data, variables) => {
      if (variables.customerId) {
        queryClient.invalidateQueries({ queryKey: customerPreviewKeys.subscription(variables.customerId) });
        queryClient.invalidateQueries({ queryKey: customerPreviewKeys.usageSummary(variables.customerId) });
        queryClient.invalidateQueries({
          queryKey: subscriptionPlanOptionsKeys.list({ customerId: variables.customerId }),
        });
      }
    },
  });
}

export function useSelectCustomerSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customerId,
      ...request
    }: { customerId: string } & SelectSubscriptionRequest) =>
      providerCustomerPreviewService.selectSubscription(customerId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: customerPreviewKeys.subscription(variables.customerId) });
      queryClient.invalidateQueries({ queryKey: customerPreviewKeys.usageSummary(variables.customerId) });
      queryClient.invalidateQueries({ queryKey: [...customerPreviewKeys.all, 'usage-records'] });
      queryClient.invalidateQueries({ queryKey: customerPreviewKeys.invoices(variables.customerId) });
      queryClient.invalidateQueries({ queryKey: customerPreviewKeys.paymentMethod(variables.customerId) });
      queryClient.invalidateQueries({
        queryKey: subscriptionPlanOptionsKeys.list({ customerId: variables.customerId }),
      });
    },
  });
}

export function useIssuePayLink() {
  return useMutation<PayLinkResponse, unknown, { subscriptionId: string }>({
    mutationFn: ({ subscriptionId }) => providerCustomerPreviewService.issuePayLink(subscriptionId),
  });
}

export type { UsageRecordsQuery };
